import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Col, Row, Select, Switch, ProgressProps, Progress } from 'antd';
import { connect } from 'react-redux';
import { getGeiYearsByOrganizationId, getCategoriesByOrganizationIdAndYear, getCategoriesByOrganizationUnifiedIdAndYear, getPortafolioGeiYearsByOrganizationId } from 'src/store/Results/thunks';
import { getAllOrganization } from 'src/store/organization/thunks';
import { getUserByToken, getUserByTokenAll } from 'src/store/user/thunks';
import { AppState } from 'src/store';
import { Organization } from '../../../store/organization/types';
import { CategoryResponse, CategoryResults, ResultsResponse } from 'src/store/Results/types';
import style from './Results.module.scss';
import styles from 'src/globals/Layout/Admin/AdminHeader.module.scss';
import { useHistory, useParams } from 'react-router-dom';
import Chart from 'chart.js/auto';
import { Bar, Doughnut } from 'react-chartjs-2';
import ResultsHeader from './ResultsHeader';
import ResultsPowerBiButton from './ResultsPowerBiButton';
import ResultsVersionSelect, { ResultsVersionSelection } from './ResultsVersionSelect';
import { CURRENT_VERSION_VALUE } from 'src/geiVersion/types';
import { resultsYearSelectValue } from 'src/geiVersion/display';
import { FileExcelOutlined } from '@ant-design/icons';
import { openNotification } from 'src/globals/Components/Notification/Notificaction';
import * as XLSX from 'xlsx';
import { DataTypePortafolio } from '../Tables/GeiPortafolio/TPortafolioOrganizations';
import { GeiInventory, GeiInventoryPortafolioResponse } from 'src/store/geiInventory/types';
import AdminHeader from 'src/globals/Layout/Admin/AdminHeader';

// ---------- T04 grouping types ----------
interface GroupedRow {
    groupKey: string;           // `${categoryId}||${flow}`
    emissionSource: string;     // Case B: original source name; Case A: Flow
    flow: string;
    tco2: string;
    tch4: string;
    tn26: string;
    thfc: string;
    tcO2e: string;
    input: string;              // recomputed from grouped TCO2e
    children: CategoryResults[];
    isSingleChild: boolean;
}
// ----------------------------------------

// ---------- T08 Biomass helpers ----------
const isBiomassFlow = (flow: string | undefined): boolean => {
    if (!flow) return false;
    const f = flow.toLowerCase().trim();
    return f === 'biomasa' || f === 'biogenico';
};

const BIOMASS_CO2_TOOLTIP = 'El CO₂ asociado a Biomasa se excluye del cálculo para esta tabla (Valor = 0)';

interface BiomassHintProps {
    label: string | undefined;
    mutedCell?: boolean;
    fontSize?: number;
}

const BiomassHint: React.FC<BiomassHintProps> = ({ label, mutedCell, fontSize }) => {
    const [visible, setVisible] = useState(false);
    return (
        <span
            className={mutedCell ? style['biomass-co2-cell'] : style['biomass-flow-label']}
            style={fontSize ? { fontSize } : undefined}
            onMouseEnter={() => setVisible(true)}
            onMouseLeave={() => setVisible(false)}
            onFocus={() => setVisible(true)}
            onBlur={() => setVisible(false)}
            onClick={(e) => e.stopPropagation()}
        >
            <span>{label}</span>
            <span
                className={style['biomass-co2-icon']}
                role="img"
                aria-label="Biomasa: CO₂ excluido de sumatoria"
            >
                i
            </span>
            {visible && (
                <span
                    className={style['biomass-co2-tooltip']}
                    role="tooltip"
                    aria-hidden="true"
                >
                    {BIOMASS_CO2_TOOLTIP}
                </span>
            )}
        </span>
    );
};
// -----------------------------------------

interface ResultProps {
    getGeiYearsByOrganizationId?: any;
    getAllOrganization?: any;
    getUserByTokenAll?: any;
    getCategoriesByOrganizationUnifiedIdAndYear: any;
    oneResult: boolean;
    organization?: DataTypePortafolio | undefined;
    typeResult?: string;
    getPortafolioGeiYearsByOrganizationId?: any;
}





const ResultsUnified: React.FC<ResultProps> = (props: ResultProps) => {
    const [emissionSChart, setEmissionSChart] = useState([]);
    const [emissiontco2eChart, setEmissiontco2eChart] = useState([]);
    const [dataEmissionChart, setDataEmissionChart] = useState([]);
    const [nameChart, setNameChart] = useState([]);
    const [dataChart, setDataChart] = useState([]);
    const [isYearSelected, setIsYearSelected] = useState(false);
    const [organization, setOrganization] = useState([]);
    const history = useHistory();
    const [typeOrganization, setTypeOrganization] = useState("");
    const [typeYears, setTypeYears] = useState("");
    const [selectedVersion, setSelectedVersion] = useState(CURRENT_VERSION_VALUE);
    const [versionMeta, setVersionMeta] = useState<{ label: string; state?: string; consolidated?: boolean }>({ label: '', consolidated: true });
    const [category, setCategory] = useState<CategoryResponse[]>([]);
    const [isNone, setIsNone] = useState("-none");
    const [results, setResults] = useState<ResultsResponse>();
    const [pieChartData, setPieChartData] = useState<any>(null);
    const { Option } = Select;
    interface RouteParams {
        type: string
    }
    const params = useParams<RouteParams>();
    const [year, setDataYears] = useState<GeiInventoryPortafolioResponse[]>([]);
    const chartRef = useRef<HTMLCanvasElement | null>(null); // Ref para el lienzo del gráfico
    const [isRol, setIsRol] = useState('');
    const [isGrouped, setIsGrouped] = useState(true);
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

    // ---------- T04: build grouped structures per CategoryResponse ----------
    const groupedCategory = useMemo<Map<string, GroupedRow[]>>(() => {
        const result = new Map<string, GroupedRow[]>();
        if (!category || category.length === 0) return result;

        const grandTotal = parseFloat(results?.totalEmissiontco2e ?? '0');

        for (const cat of category) {
            const groups: GroupedRow[] = [];
            // O(n) single pass: bucket by flow within this category
            const buckets = new Map<string, CategoryResults[]>();
            for (const row of cat.categoryResults) {
                const key = row.flow ?? '';
                if (!buckets.has(key)) buckets.set(key, []);
                buckets.get(key)!.push(row);
            }

            for (const [flow, children] of Array.from(buckets)) {
                const sumNum = (sel: (r: CategoryResults) => string | undefined): number =>
                    children.reduce((acc: number, r: CategoryResults) => acc + (parseFloat(sel(r) ?? '0') || 0), 0);

                const groupedTco2e = sumNum(r => r.tcO2e);
                const inputPct = grandTotal > 0
                    ? Math.round((groupedTco2e / grandTotal) * 100).toString()
                    : '0';

                const distinctSourceNames: string[] = [];
                for (const child of children) {
                    const name = child.emissionSource ?? '';
                    if (distinctSourceNames.indexOf(name) === -1) {
                        distinctSourceNames.push(name);
                    }
                }
                const isSingleSource = distinctSourceNames.length === 1;
                const soleSourceName = isSingleSource ? (distinctSourceNames[0] || '') : '';
                const displaySource = (isSingleSource && soleSourceName)
                    ? soleSourceName
                    : (flow || children[0]?.emissionSource);

                groups.push({
                    groupKey: `${cat.id}||${flow}`,
                    emissionSource: displaySource,
                    flow,
                    tco2:  sumNum(r => r.tco2).toFixed(3),
                    tch4:  sumNum(r => r.tch4).toFixed(3),
                    tn26:  sumNum(r => r.tn26).toFixed(3),
                    thfc:  sumNum(r => r.thfc).toFixed(3),
                    tcO2e: groupedTco2e.toFixed(3),
                    input: inputPct,
                    children,
                    isSingleChild: isSingleSource,
                });
            }
            result.set(cat.id, groups);
        }
        return result;
    }, [category, results?.totalEmissiontco2e]);

    const toggleGroup = (key: string) => {
        setExpandedGroups(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };
    // -----------------------------------------------------------------------

    const init = async () => {
        setIsNone("-none");
        setCategory([]);
        setIsYearSelected(false);
        setDataChart([]);
        setTypeYears("")

        setNameChart([]);
        setEmissionSChart([])
        setEmissiontco2eChart([])
        setDataEmissionChart([])
        const responseUser = await props.getUserByTokenAll();
        setIsRol(responseUser.rol)
        if (props.oneResult) {
            let responseData; // Usamos let para poder reasignar
            setTypeOrganization(props.organization == undefined ? "" : props.organization?.id);
            switch (props.typeResult) {
                case "PORTAFOLIO":
                    // Aquí podrías asignar algo si corresponde
                    responseData = await props.getPortafolioGeiYearsByOrganizationId(
                        props.organization?.id
                    );
                    break;

                case "GEIINVENTARIO":
                    responseData = await props.getGeiYearsByOrganizationId(
                        props.organization?.id
                    );
                    break;

                default:
                    break;
            }
            if (responseData !== undefined) {
                setDataYears(responseData);
            } else {
                setDataYears([]);
                setIsNone("-none");
                setTypeYears("");
            }

        } else {
            const responseDataOrganization = await props.getAllOrganization();
            setOrganization(responseDataOrganization);
        }

    };

    useEffect(() => {
        init();
    }, [props.organization]);


    const handleChangeTypeOrganization = async (value: any) => {
        setTypeOrganization(value);
        setSelectedVersion('');
        setVersionMeta({ label: '', consolidated: true });
        setResults(undefined);
        setCategory([]);
        setIsYearSelected(false);
        setIsNone("-none");
        const responseData = await props.getGeiYearsByOrganizationId(value);
        if (responseData !== undefined) {
            setDataYears(responseData);
        } else {
            setDataYears([]);
            setIsNone("-none");
            setTypeYears("");
        }
    };
    const handleChangeTypeYears = async (value: any, versionId?: string, resetVersion = true) => {
        const yearsResponse: GeiInventoryPortafolioResponse | undefined = year.find((values: GeiInventoryPortafolioResponse) => values.id == value)
            || year.find((values: GeiInventoryPortafolioResponse) => String(values.year) == String(value));
        const yearText = yearsResponse == undefined ? "" : yearsResponse.year.toString();
        if (resetVersion) {
            setSelectedVersion('');
            setVersionMeta({ label: '', consolidated: true });
            setTypeYears(yearText);
            setResults(undefined);
            setCategory([]);
            setIsYearSelected(false);
            setIsNone("-none");
            return;
        }
        setTypeYears(yearText);
        setResults(undefined);
        setCategory([]);
        setIsYearSelected(false);
        setIsNone("-none");
        const responseData = await props.getCategoriesByOrganizationUnifiedIdAndYear(typeOrganization, yearText, props.typeResult || 'GEI', versionId);
        if (responseData == undefined) {
            return;
        }
        if (responseData != undefined) {
            setIsNone("")
            setCategory(responseData.categoryResponse);
            setResults(responseData);
        }

        setIsNone("");
        setCategory(responseData.categoryResponse);
        setResults(responseData);
        setIsYearSelected(true);
        const emissionS = responseData.categoryResponse.flatMap((category: any) =>
            category.categoryResults.map((result: any) => result.emissionSource)//Fuentes de Emision
        );

        const emissiontco2e = responseData.categoryResponse.flatMap((category: any) =>
            category.categoryResults.map((result: any) => result.tCO2e)//Fuentes de Emision
        );

        console.log(emissionS)
        const dataEmission = responseData.categoryResponse.flatMap((category: any) =>
            category.categoryResults.map((result: any) => parseFloat(result.tcO2e)));

        const dataEmissionInteger = dataEmission.map((value: number) => Math.floor(value));

        console.log(dataEmissionInteger)
        console.log(dataEmission)
        setEmissionSChart(emissionS);
        setDataEmissionChart(dataEmission);
        setEmissiontco2eChart(emissiontco2e);

        //obtenemos las categorias para la seccion 2
        const names = responseData.categoryResponse.map((item: any) => item.name);
        console.log(names)
        //obtenemos los valores de tCo2e para la seccion 2
        const data = responseData.categoryResponse.map((item: any) => item.totalinput);
        setNameChart(names);
        setDataChart(data);
    };
    const randomColor = () => '#' + Math.floor(Math.random() * 16777215).toString(16);
    const backgroundColors = dataEmissionChart.map(() => randomColor());
    const conicColors: ProgressProps['strokeColor'] = {
        '0%': '#87d068',
        '50%': '#ffe58f',
        '100%': '#ffccc7',
    };
    const twoColors: ProgressProps['strokeColor'] = {
        '0%': '#108ee9',
        '100%': '#87d068',
    };

    let dataBar1 = {
        labels: emissionSChart,
        datasets: [
            {
                label: 'Fuentes de Emisión X tCO2e:',
                data: dataEmissionChart,
                backgroundColor: backgroundColors
            },
        ],
    };
    const options = {
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    stepSize: 100, // Establece el tamaño del paso en el eje Y
                    max: 10000, // Establece el valor máximo en el eje Y
                },
            },
        },
        plugins: {
            legend: {
                display: false
            },
        },
    };

    let dataPie2 = {
        labels: nameChart,
        datasets: [
            {
                data: dataChart
            },
        ],
    };
    let dataBar2 = {
        labels: nameChart,
        datasets: [
            {
                label: 'TOTAL CO2e(t)',
                data: dataChart
            },
        ],
    };

    const handleExportToExcel = () => {
        const exportData = category.map((categoryResponse: CategoryResponse) => {
            return categoryResponse.categoryResults.map((categoryResult: CategoryResults) => ({
                'Categoria': categoryResponse.name,
                'Establecimientos': categoryResult.establisment,
                'Fuente de emisión': categoryResult.emissionSource,
                'Flujo de Fuente': categoryResult.flow,
                't CO₂': categoryResult.tco2,
                't CH₄': categoryResult.tch4,
                't N₂O': categoryResult.tn26,
                't HFC': categoryResult.thfc,
                't CO₂e': categoryResult.tcO2e,
                'Aporte%': categoryResult.input,
            }));
        }).flat();
        const exportDataTotal = category.map((categoryResponse: CategoryResponse) => ({
            'Nombre': categoryResponse.name,
            'Tipo': categoryResponse.type,
            'Descripción': categoryResponse.description,
            'Total tco2': categoryResponse.totaltco2,
            'Total tch4': categoryResponse.totaltch4,
            'Total tn2o': categoryResponse.totaltn2o,
            'Total thfc': categoryResponse.totalthfc,
            'Total tco2e': categoryResponse.totaltco2e,
            'Total input': categoryResponse.totalinput,
        })).flat();

        const exportDataResults = {
            'Total tco2': results?.totalEmissiontco2,
            'Total tch4': results?.totalEmissiontch4,
            'Total tn2o': results?.totalEmissiontn2o,
            'Total thfc': results?.totalEmissionthfc,
            'Total tco2e': results?.totalEmissiontco2e,
            'Total input': results?.totalEmissioninput,
            'Total Biomasa': results?.totalEmissionBiomasa,
            'Total Hcfc': results?.totalHcfc,
        };
        const exportDataResultsArray = [exportDataResults];

        //results
        const ws = XLSX.utils.json_to_sheet(exportData);
        const ws2 = XLSX.utils.json_to_sheet(exportDataTotal);
        const ws3 = XLSX.utils.json_to_sheet(exportDataResultsArray);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Fuentes');
        XLSX.utils.book_append_sheet(wb, ws2, 'Total');
        XLSX.utils.book_append_sheet(wb, ws3, 'Resultados');

        const excelFileName = 'Resultados.xlsx';
        XLSX.writeFile(wb, excelFileName);

        openNotification("Exportación exitosa", `Los datos se han exportado a ${excelFileName}`);
    };

    useEffect(() => {
        if (chartRef.current && pieChartData) {
            const ctx = chartRef.current.getContext('2d');
            if (ctx) {
                new Chart(ctx, {
                    type: 'doughnut',
                    data: pieChartData,
                });
            }
        }
    }, [pieChartData]);

    return (
        <div>
            {/* <ResultsHeader unified={"-bold"} mensual={""} history={""} /> */}
            <AdminHeader>Resultados anuales</AdminHeader>
            <Row justify="center" style={{ width: "100%" }}>
                <Col lg={2}></Col>
                {!props.oneResult && <Col lg={8}>
                    Escoga la organización:
                    <Select
                        showSearch
                        style={{ width: "100%" }}
                        onChange={handleChangeTypeOrganization}
                        filterOption={(input: any, option: any) =>
                            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                        value={typeOrganization}
                        placeholder="Escoga la organización"
                    >
                        {organization.map((type: Organization) => (
                            <Option key={`type-option-${type}`} value={type.id}
                            >
                                {type.ruc + "-" + type.name}
                            </Option>
                        ))}
                    </Select>
                </Col>}
                <Col lg={4}>
                    Escoga el año
                    <Select

                        showSearch
                        style={{ width: "100%" }}
                        onChange={(value) => { handleChangeTypeYears(value); }}
                        filterOption={(input: any, option: any) =>
                            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                        value={typeYears}
                        placeholder="Escoga el año"
                    >
                        {Array.from(new Set(year.map((type: unknown) => resultsYearSelectValue(type)))).filter(Boolean).map((yearValue) => (
                            <Option key={`type-option-${yearValue}`} value={yearValue}
                            >
                                {yearValue}
                            </Option>
                        ))}
                    </Select>
                </Col>
                <Col lg={4}>
                    {typeYears ? (
                        <ResultsVersionSelect
                            organizationId={typeOrganization}
                            year={String(typeYears)}
                            value={selectedVersion}
                            onChange={(selection: ResultsVersionSelection) => {
                                setSelectedVersion(selection.versionId || CURRENT_VERSION_VALUE);
                                setVersionMeta({ label: selection.label, state: selection.state, consolidated: selection.consolidated });
                                handleChangeTypeYears(typeYears, selection.versionId, false);
                            }}
                        />
                    ) : null}
                </Col>
                <Col lg={2}></Col>
                <Col lg={4} style={{ display: 'flex', alignItems: 'flex-end', gap: 8, paddingBottom: 4 }}>
                    <Switch
                        checked={isGrouped}
                        onChange={setIsGrouped}
                        size="small"
                        style={{ backgroundColor: isGrouped ? '#388044' : undefined }}
                    />
                    <span style={{ fontSize: 13, color: '#555', userSelect: 'none' }}>Vista agrupada</span>
                </Col>
            </Row>
            <Row justify={"end"} align="middle">
                <Col>
                    <ResultsPowerBiButton organizationId={typeOrganization} />
                    <Button
                        type="primary"
                        icon={<FileExcelOutlined />}
                        style={{ backgroundColor: '#1f71d9', borderColor: 'gray', marginLeft: '8px' }}
                        onClick={handleExportToExcel}
                    >
                        Exportar a Excel
                    </Button>

                </Col>

            </Row>

            {selectedVersion && !versionMeta.consolidated ? (
                <div style={{ margin: '8px 0' }}>
                    Mostrando {versionMeta.label}
                    {versionMeta.state === 'CLOSED' ? ' · histórica, solo lectura' : ''}
                </div>
            ) : null}
            <div className={style["title-reults"]} >
                1. DECLARACIÓN DE EMISIONES DE GEI POR ESTABLECIMIENTO
            </div>
            <Row justify="start" className={style["custom-row" + isNone]}>
                <Col lg={6}>
                    Fuente de emisión
                </Col>
                <Col lg={3}>
                    Flujo de Fuente
                </Col>
                <Col lg={2}>
                    t CO₂
                </Col>
                <Col lg={2}>
                    t CH₄
                </Col>
                <Col lg={2}>
                    t N₂O
                </Col>
                <Col lg={2}>
                    t HFC
                </Col>
                <Col lg={2}>
                    t CO₂e
                </Col>
                <Col lg={2}>
                    Aporte%
                </Col>

            </Row>
            {category.map((cat: CategoryResponse) => (
                <div key={cat.id}>
                    {/* ---- GROUPED VIEW (Section 1) ---- */}
                    {isGrouped && (groupedCategory.get(cat.id) ?? []).map((group: GroupedRow) => {
                        const isExpanded = expandedGroups.has(group.groupKey);
                        return (
                            <div key={group.groupKey}>
                                <Row justify="start" className={style["custom-row-data" + isNone]}
                                    style={{ backgroundColor: group.isSingleChild ? undefined : '#f6fff7', cursor: group.isSingleChild ? 'default' : 'pointer' }}
                                    onClick={group.isSingleChild ? undefined : () => toggleGroup(group.groupKey)}
                                >
                                    <Col lg={6} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        {!group.isSingleChild && (
                                            <span style={{ fontSize: 11, color: '#388044', minWidth: 14 }}>
                                                {isExpanded ? '▼' : '▶'}
                                            </span>
                                        )}
                                        <strong>{group.emissionSource}</strong>
                                        {!group.isSingleChild && (
                                            <span style={{ fontSize: 11, color: '#888' }}>
                                                ({group.children.length})
                                            </span>
                                        )}
                                    </Col>
                                    <Col lg={3}>{group.flow}</Col>
                                    <Col lg={2}>
                                        {isBiomassFlow(group.flow)
                                            ? <BiomassHint label={group.tco2} mutedCell />
                                            : group.tco2}
                                    </Col>
                                    <Col lg={2}>{group.tch4}</Col>
                                    <Col lg={2}>{group.tn26}</Col>
                                    <Col lg={2}>{group.thfc}</Col>
                                    <Col lg={2}>{group.tcO2e}</Col>
                                    <Col lg={2}>
                                        <Progress steps={12} percent={Number.parseInt(group.input)} size={20} strokeColor={conicColors} />
                                    </Col>
                                </Row>
                                {/* Expanded child rows */}
                                {!group.isSingleChild && isExpanded && group.children.map((child: CategoryResults, ci: number) => (
                                    <Row key={`${group.groupKey}-child-${ci}`} justify="start" className={style["custom-row-data" + isNone]}
                                        style={{ backgroundColor: '#fafafa', paddingLeft: 28 }}>
                                        <Col lg={6} style={{ paddingLeft: 20, color: '#555', fontSize: 12 }}>
                                            {child.emissionSource}
                                        </Col>
                                        <Col lg={3} style={{ fontSize: 12 }}>{child.flow}</Col>
                                        <Col lg={2} style={{ fontSize: 12 }}>
                                            {isBiomassFlow(child.flow)
                                                ? <BiomassHint label={child.tco2} mutedCell fontSize={12} />
                                                : child.tco2}
                                        </Col>
                                        <Col lg={2} style={{ fontSize: 12 }}>{child.tch4}</Col>
                                        <Col lg={2} style={{ fontSize: 12 }}>{child.tn26}</Col>
                                        <Col lg={2} style={{ fontSize: 12 }}>{child.thfc}</Col>
                                        <Col lg={2} style={{ fontSize: 12 }}>{child.tcO2e}</Col>
                                        <Col lg={2} style={{ fontSize: 12 }}>
                                            <Progress steps={12} percent={Number.parseInt(child.input)} size={16} strokeColor={conicColors} />
                                        </Col>
                                    </Row>
                                ))}
                            </div>
                        );
                    })}

                    {/* ---- FLAT VIEW (Section 1) ---- */}
                    {!isGrouped && cat.categoryResults.map((row: CategoryResults, ri: number) => (
                        <div key={`flat-s1-${cat.id}-${ri}`}>
                            <Row justify="start" className={style["custom-row-data" + isNone]}>
                                <Col lg={6}>{row.emissionSource}</Col>
                                <Col lg={3}>{row.flow}</Col>
                                <Col lg={2}>
                                    {isBiomassFlow(row.flow)
                                        ? <BiomassHint label={row.tco2} mutedCell />
                                        : row.tco2}
                                </Col>
                                <Col lg={2}>{row.tch4}</Col>
                                <Col lg={2}>{row.tn26}</Col>
                                <Col lg={2}>{row.thfc}</Col>
                                <Col lg={2}>{row.tcO2e}</Col>
                                <Col lg={2}>
                                    <Progress steps={12} percent={Number.parseInt(row.input)} size={20} strokeColor={conicColors} />
                                    <div></div>
                                </Col>
                            </Row>
                        </div>
                    ))}

                    {/* Category subtotal — always from backend, unchanged */}
                    <Row justify="start" className={style["custom-row-total" + isNone]}>
                        <Col lg={9}>{"Categoria " + cat.type + " (" + cat.name + ")"}</Col>
                        <Col lg={2}>{cat.totaltco2}</Col>
                        <Col lg={2}>{cat.totaltch4}</Col>
                        <Col lg={2}>{cat.totaltn2o}</Col>
                        <Col lg={2}>{cat.totalthfc}</Col>
                        <Col lg={2}>{cat.totaltco2e}</Col>
                        <Col lg={2}>
                            <Progress percent={Number.parseInt(cat.totalinput)} size={[200, 20]} strokeColor="green" />
                        </Col>
                    </Row>
                </div>
            ))}
            <Row justify="start" className={style["custom-row-total" + isNone]}>
                <Col lg={9}>
                    Total Emisiones CO₂e (t)
                </Col>
                <Col lg={2}>
                    {results?.totalEmissiontco2}
                </Col>
                <Col lg={2}>
                    {results?.totalEmissiontch4}
                </Col>
                <Col lg={2}>
                    {results?.totalEmissiontn2o}
                </Col>
                <Col lg={2}>
                    {results?.totalEmissionthfc}
                </Col>
                <Col lg={2}>
                    {results?.totalEmissiontco2e}
                </Col>
                <Col lg={2}>
                    {isYearSelected
                        ?
                        Number.parseInt(results!.totalEmissioninput) < 100
                            ?
                            <Progress steps={5} percent={Number.parseInt(results!.totalEmissioninput)} size={20} strokeColor='green' />
                            :
                            <div><Progress steps={5} percent={Number.parseInt(results!.totalEmissioninput)} size={20} strokeColor='green' /> {results!.totalEmissioninput}</div>

                        :
                        <div></div>
                    }
                </Col>
            </Row>

            {props.typeResult == "PORTAFOLIO" && <Row justify="start" className={style["custom-row-total" + isNone]}>
                <Col lg={9}>
                    Total Emisiones CO₂e (t) x FA
                </Col>
                <Col lg={2}>
                    {results?.totalEmissiontco2FA}
                </Col>
                <Col lg={2}>
                    {results?.totalEmissiontch4FA}
                </Col>
                <Col lg={2}>
                    {results?.totalEmissiontn2oFA}
                </Col>
                <Col lg={2}>
                    {results?.totalEmissionthfcFA}
                </Col>
                <Col lg={2}>
                    {results?.totalEmissiontco2eFA}
                </Col>
                
            </Row>
            }


            <Row justify="start" className={style["custom-row-data-total" + isNone]}>
                <Col lg={12}>
                    Emisiones biogénicas por combustión de biomasa CO₂ (t)
                </Col>
                <Col lg={12}>
                    {results?.totalEmissionBiomasa}
                </Col>


            </Row>
            <Row justify="start" className={style["custom-row-data-total" + isNone]}>
                <Col lg={12}>
                    Emisiones HCFC
                </Col>
                <Col lg={12}>
                    {results?.totalHcfc}
                </Col>
            </Row>
            <div className={style["title-reults"]} >
                2. HUELLA DE CARBONO POR CATEGORÍA
            </div>
            <Row justify="start" >
                <Col lg={13}>
                    <Row justify="start" className={style["custom-row" + isNone]}>
                        <Col lg={12}>
                            Categoria
                        </Col>
                        <Col lg={5}>
                            TOTAL CO₂e (t)
                        </Col>
                        <Col lg={5}>
                            Aporte %
                        </Col>
                    </Row>
                    {category.map((type: CategoryResponse) => (
                        <Row justify="start" className={style["custom-row-data" + isNone]}>
                            <Col lg={12}>
                                {"Categoria " + type.type + " (" + type.name + ")"}
                            </Col>
                            <Col lg={5}>
                                {type.totaltco2e}
                            </Col>
                            <Col lg={5}>

                                <Progress type="dashboard" percent={Number.parseInt(type.totalinput)} strokeColor={conicColors} />
                            </Col>
                        </Row>
                    ))}
                </Col>
                <Col lg={5}>
                    {isYearSelected
                        ?
                        <div className={style["chart-reultsPie"]}>
                            <Doughnut data={dataPie2} />
                        </div>
                        :
                        <div>
                        </div>
                    }
                </Col>
            </Row>

            <div className={style["title-reults"]} >
                3. MATERIALIDAD
            </div>
            <Row justify="start" className={style["custom-row" + isNone]}>
                <Col lg={4}>
                    Fuente de emisión
                </Col>
                <Col lg={4}>
                    Flujo de Fuente
                </Col>
                <Col lg={4}>
                    Categoria
                </Col>
                <Col lg={4}>
                    t CO₂e
                </Col>
                <Col lg={4}>
                    Aporte %
                </Col>
            </Row>
            {category.map((cat: CategoryResponse) => (
                <div key={`s3-cat-${cat.id}`}>
                    {/* ---- GROUPED VIEW (Section 3) ---- */}
                    {isGrouped && (groupedCategory.get(cat.id) ?? []).map((group: GroupedRow) => {
                        const isExpanded = expandedGroups.has(group.groupKey);
                        return (
                            <div key={`s3-${group.groupKey}`}>
                                <Row justify="start" className={style["custom-row-data" + isNone]}
                                    style={{ backgroundColor: group.isSingleChild ? undefined : '#f6fff7', cursor: group.isSingleChild ? 'default' : 'pointer' }}
                                    onClick={group.isSingleChild ? undefined : () => toggleGroup(group.groupKey)}
                                >
                                    <Col lg={4} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        {!group.isSingleChild && (
                                            <span style={{ fontSize: 11, color: '#388044', minWidth: 14 }}>
                                                {isExpanded ? '▼' : '▶'}
                                            </span>
                                        )}
                                        <strong>{group.emissionSource}</strong>
                                        {!group.isSingleChild && (
                                            <span style={{ fontSize: 11, color: '#888' }}>({group.children.length})</span>
                                        )}
                                    </Col>
                                    <Col lg={4}>
                                        {isBiomassFlow(group.flow)
                                            ? <BiomassHint label={group.flow} />
                                            : group.flow}
                                    </Col>
                                    <Col lg={4}>{cat.type}</Col>
                                    <Col lg={4}>{group.tcO2e}</Col>
                                    <Col lg={4}>
                                        <Progress steps={25} percent={Number.parseInt(group.input)} size={20} strokeColor={conicColors} />
                                    </Col>
                                </Row>
                                {!group.isSingleChild && isExpanded && group.children.map((child: CategoryResults, ci: number) => (
                                    <Row key={`s3-${group.groupKey}-child-${ci}`} justify="start" className={style["custom-row-data" + isNone]}
                                        style={{ backgroundColor: '#fafafa' }}>
                                        <Col lg={4} style={{ paddingLeft: 24, color: '#555', fontSize: 12 }}>{child.emissionSource}</Col>
                                        <Col lg={4} style={{ fontSize: 12 }}>
                                            {isBiomassFlow(child.flow)
                                                ? <BiomassHint label={child.flow} fontSize={12} />
                                                : child.flow}
                                        </Col>
                                        <Col lg={4} style={{ fontSize: 12 }}>{cat.type}</Col>
                                        <Col lg={4} style={{ fontSize: 12 }}>{child.tcO2e}</Col>
                                        <Col lg={4} style={{ fontSize: 12 }}>
                                            <Progress steps={25} percent={Number.parseInt(child.input)} size={16} strokeColor={conicColors} />
                                        </Col>
                                    </Row>
                                ))}
                            </div>
                        );
                    })}

                    {/* ---- FLAT VIEW (Section 3) ---- */}
                    {!isGrouped && cat.categoryResults.map((row: CategoryResults, ri: number) => (
                        <div key={`flat-s3-${cat.id}-${ri}`}>
                            <Row justify="start" className={style["custom-row-data" + isNone]}>
                                <Col lg={4}>{row.emissionSource}</Col>
                                <Col lg={4}>
                                    {isBiomassFlow(row.flow)
                                        ? <BiomassHint label={row.flow} />
                                        : row.flow}
                                </Col>
                                <Col lg={4}>{cat.type}</Col>
                                <Col lg={4}>{row.tcO2e}</Col>
                                <Col lg={4}>
                                    <Progress steps={25} percent={Number.parseInt(row.input)} size={20} strokeColor={conicColors} />
                                </Col>
                            </Row>
                        </div>
                    ))}
                </div>
            ))}

        </div>

    );
    { }
};
const mapStateToProps = (state: AppState) => ({

});

export default connect(mapStateToProps, {
    getGeiYearsByOrganizationId, getCategoriesByOrganizationIdAndYear, getCategoriesByOrganizationUnifiedIdAndYear,
    getAllOrganization, getUserByTokenAll, getPortafolioGeiYearsByOrganizationId
})(ResultsUnified);